/**
 * useEstimatesUpload.ts
 * Calls POST /api/estimates-upload/upload with an Estimates Excel file.
 * Data is parsed server-side and upserted into EF_Exclusion.
 */

import { useState, useCallback } from 'react';
import useSession from '@hooks/useSession';
import { getRuntimeEnv } from '@utils/runtimeEnv';
import { appInsights } from '../appInsights';

const API_BASE =
  getRuntimeEnv('REACT_APP_OATT_API_BASE') ||
  `${getRuntimeEnv('REACT_APP_SERVER_PROTOCOL')}://${getRuntimeEnv('REACT_APP_SERVER_HOST')}/api`;

// ── Types ──────────────────────────────────────────────────────────────────

export interface SkippedRow {
  rowNumber: number;
  meterNumber: string | null;
  reason: string;
}

export interface EstimatesUploadData {
  monthYear: string;
  totalRows: number;
  inserted: number;
  skipped: number;
  skippedRows: SkippedRow[];
}

export interface EstimatesUploadResult {
  success: boolean;
  data?: EstimatesUploadData;
  error?: string;
}

interface EstimatesUploadState {
  uploading: boolean;
  result: EstimatesUploadResult | null;
  error: string | null;
}

// ── Hook ──────────────────────────────────────────────────────────────────

export function useEstimatesUpload() {
  const { accessToken } = useSession();

  const [state, setState] = useState<EstimatesUploadState>({
    uploading: false,
    result: null,
    error: null,
  });

  const uploadFile = useCallback(
    async (file: File): Promise<boolean> => {
      if (!file) {
        setState((s) => ({ ...s, error: 'No file selected.' }));
        return false;
      }

      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!['xlsx', 'xls', 'xlsm'].includes(ext)) {
        setState((s) => ({ ...s, error: 'Only .xlsx, .xls, and .xlsm files are accepted.' }));
        return false;
      }

      setState({ uploading: true, result: null, error: null });

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE}/estimates-upload/upload`, {
          method: 'POST',
          body: formData,
          headers: { Authorization: `Bearer ${accessToken}` },
          // Do NOT set Content-Type — browser sets multipart boundary automatically
        });

        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.includes('application/json')) {
          const text = await response.text();
          throw new Error(`Server error (${response.status}): ${text.slice(0, 300)}`);
        }

        const data: EstimatesUploadResult = await response.json();

        if (!response.ok) {
          throw new Error(data.error || `Upload failed (${response.status})`);
        }

        setState({ uploading: false, result: data, error: null });
        if (data.success) {
          appInsights?.trackEvent({
            name: 'EstimatesUploadSuccess',
            properties: { fileName: file.name, monthYear: data.data?.monthYear ?? '' },
          });
        } else {
          appInsights?.trackEvent({
            name: 'EstimatesUploadFailure',
            properties: { fileName: file.name, error: data.error ?? '' },
          });
        }
        return data.success === true;
      } catch (err: any) {
        const msg = err?.message ?? 'Upload failed. Please try again.';
        setState({ uploading: false, result: null, error: msg });
        appInsights?.trackEvent({
          name: 'EstimatesUploadFailure',
          properties: { fileName: file.name, error: msg },
        });
        return false;
      }
    },
    [accessToken]
  );

  const reset = useCallback(() => {
    setState({ uploading: false, result: null, error: null });
  }, []);

  return { ...state, uploadFile, reset };
}
